import { Users, ArrowRightLeft, FlaskConical, LineChart, MessageSquare, Check, ShieldCheck, Eye, History, Target, Repeat, type LucideIcon } from "lucide-react";
import { motion } from "motion/react";
import { SectionLabel, H2, Body } from "../shared/Typography";
import { useSectionParallax } from "../../hooks/useSectionParallax";

const VIOLET = "text-[#5B21B6] dark:text-[#B9A5FF]";
const CARD = "rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] p-8 md:p-10";

function ServiceHeading({ icon: Icon, name, title, accent = VIOLET }: { icon: LucideIcon; name: string; title: string; accent?: string }) {
  return (
    <>
      <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${accent}`}><Icon size={16} />{name}</p>
      <h3 className="mt-3 mb-4 text-2xl md:text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">{title}</h3>
    </>
  );
}

export function AthenaEigen() {
  const { ref, bgY } = useSectionParallax();
  return (
    <motion.div ref={ref} style={{ y: bgY }} className="grid lg:grid-cols-2 gap-6">
      <div className={CARD}>
        <ServiceHeading icon={Users} name="Athena" title="Digital twins that follow your criteria." />
        <Body>
          Eligibility criteria change during protocol development. Athena generates digital twin cohorts
          from your inclusion and exclusion criteria, version by version, so the cohort you simulate matches
          the protocol you are actually drafting. If you already have patient data, you can run your
          simulation on that instead.
        </Body>
      </div>
      <div className={CARD}>
        <ServiceHeading icon={ArrowRightLeft} name="Eigen" title="Keep the models you've built." />
        <Body className="mb-4">
          Modeling teams shouldn&apos;t have to rebuild what already works. Eigen converts existing models
          (MATLAB and SBML) into Foresight&apos;s format, so you can move from an existing model to a trial
          simulation without re-encoding it.
        </Body>
        <Body>
          Conversion is tested against published pharmacology models with known correct results. Once
          converted, the simulation engine makes no assumptions about a particular drug or disease. It runs
          whatever model you give it.
        </Body>
      </div>
    </motion.div>
  );
}

const SIMULATOR_FEATURES = [
  { title: "Trial designs", body: "3+3 dose escalation with sequential cohort expansion, and fixed dosing regimens." },
  { title: "Dosing and schedules", body: "Visit and observation schedules, dose-limiting toxicity and safety events." },
  { title: "Decision rules", body: "Escalate, hold, de-escalate or stop, applied as the trial unfolds." },
  { title: "Variability", body: "Uncertainty in drug parameters and differences between patients, so results reflect a spread of outcomes rather than a single deterministic path." },
];

export function SimulatorSection() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref} className="grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
      <motion.div style={{ y: fgY }}>
        <ServiceHeading icon={FlaskConical} name="Simulator" title="Your design, played out." />
        <Body className="mb-4">The Simulator runs the trial as the protocol describes it.</Body>
        <Body>Every job is tracked from submission to completion, and every run is tied to the exact model, design and cohort that produced it.</Body>
      </motion.div>
      <motion.div style={{ y: bgY }} className="grid sm:grid-cols-2 gap-4">
        {SIMULATOR_FEATURES.map(({ title, body }) => (
          <div key={title} className="border-t-2 border-[#5B21B6] dark:border-[#7C4DFF] pt-4">
            <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">{title}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">{body}</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

const RELIABILITY = ["How often it reaches the right decision", "How often it stops early", "How sensitive it is to your assumptions"];

export function ReliabilityBand() {
  return (
    <div className="rounded-3xl bg-[#F3EEFF] dark:bg-[#140E24] border border-[#5B21B6]/20 dark:border-[#7C4DFF]/25 p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${VIOLET}`}><Repeat size={16} />Operating characteristics</p>
        <H2 className="mt-3 mb-5">Know how reliable your design is.</H2>
        <Body>
          One simulated trial is a single story. Foresight can run the same design across many virtual
          repetitions, so you go into the real trial knowing the design&apos;s operating characteristics.
        </Body>
      </div>
      <ul className="space-y-4">
        {RELIABILITY.map((r) => (
          <li key={r} className="flex items-start gap-3 rounded-2xl bg-white/70 dark:bg-black/30 px-5 py-4 text-base text-[#0A0A0F] dark:text-white">
            <Target size={18} className={`mt-0.5 shrink-0 ${VIOLET}`} />{r}
          </li>
        ))}
      </ul>
    </div>
  );
}

const BEAM_TASKS = ["Fitting models to data", "Non-compartmental analysis", "Parameter identifiability", "Sensitivity analysis", "Profile likelihood", "In vitro–in vivo correlation"];

export function IvizBeam() {
  const { ref, bgY } = useSectionParallax();
  return (
    <motion.div ref={ref} style={{ y: bgY }} className="grid lg:grid-cols-2 gap-6">
      <div className={CARD}>
        <ServiceHeading icon={LineChart} name="iViz" title="From results to insight." accent="text-[#1E4FD8] dark:text-[#4A90D9]" />
        <Body>
          iViz runs entirely inside Foresight. Explore, visualize and annotate patient data with statistical
          analysis on a no-code canvas, or in Python and R notebooks. Results stay in the same workspace, so
          there is no export step between simulating and analyzing.
        </Body>
      </div>
      <div className={CARD}>
        <ServiceHeading icon={MessageSquare} name="BEAM" title="Analysis by conversation." />
        <Body className="mb-5">Describe the analysis you need and BEAM carries it out. It covers pharmacometric work that normally takes specialist setup:</Body>
        <div className="flex flex-wrap gap-2">
          {BEAM_TASKS.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-[#5B21B6]/25 dark:border-[#7C4DFF]/30 px-3 py-1.5 text-xs text-[#0A0A0F] dark:text-white">
              <Check size={12} className={VIOLET} />{t}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

const CHECKS: { title: string; body: string; icon: LucideIcon }[] = [
  { title: "Inputs are checked first.", body: "Models and trial setups are validated before a simulation starts, so problems surface early.", icon: ShieldCheck },
  { title: "Nothing runs unreviewed.", body: "You approve the schedule before the simulation begins.", icon: Eye },
  { title: "Every run has a record.", body: "Results trace back to the inputs that generated them.", icon: History },
  { title: "Tested against known answers.", body: "Both model conversion and the simulation engine are checked against reference cases with known results.", icon: Target },
];

export function BuiltToBeChecked() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }} className="text-center mb-12">
        <div className="flex justify-center"><SectionLabel tone="foresight">Trust</SectionLabel></div>
        <H2>Built to be checked.</H2>
      </motion.div>
      <motion.div style={{ y: bgY }}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CHECKS.map(({ title, body, icon: Icon }) => (
            <div key={title} className="border-t-2 border-[#5B21B6] dark:border-[#7C4DFF] pt-5">
              <Icon size={20} className={`${VIOLET} mb-4`} />
              <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">{title}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm font-medium text-[#0A0A0F] dark:text-white">Foresight supports design decisions. It does not replace a trial.</p>
      </motion.div>
    </div>
  );
}

